import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { fetchPreferences, updatePreferences, fetchCategories, fetchNewsCountries } from '../../redux/actions/newsAction';
import countries_lib from 'i18n-iso-countries';
import countries_en from 'i18n-iso-countries/langs/en.json';
import countries_fr from 'i18n-iso-countries/langs/fr.json';
import countries_ar from 'i18n-iso-countries/langs/ar.json';
import {
  Box,
  Typography,
  Button,
  TextField,
  Chip,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  useTheme,
  alpha,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { CategorySkeletonItem, PreferencesScreenSkeleton } from './components/NewsSkeleton';
import BackButton from '../../components/BackButton';
import { isMobile } from '../../utils/utils';

countries_lib.registerLocale(countries_en);
countries_lib.registerLocale(countries_fr);
countries_lib.registerLocale(countries_ar);

const countryFlag = (code) =>
  code
    .toUpperCase()
    .split('')
    .map((c) => String.fromCodePoint(0x1f1e6 + c.charCodeAt(0) - 65))
    .join('');

const SectionLabel = ({ children }) => {
  const theme = useTheme();
  return (
    <Typography
      variant="caption"
      sx={{
        display: 'block',
        mb: 1.5,
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        color: theme.palette.text.disabled,
      }}
    >
      {children}
    </Typography>
  );
};

const NewsPreferencesScreen = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const { preferences, categories, countries, loading, errors } = useSelector(state => state.legalNews);

  const lang = i18n.language === 'ar' ? 'ar' : i18n.language === 'fr' ? 'fr' : 'en';
  const countryLabel = (code) => countries_lib.getName(code, lang) || code;

  const [form, setForm] = useState({
    categories: [],
    keywords: [],
    countries: [],
    email_daily_digest: false,
    email_frequency: 'daily',
    notification_enabled: true,
  });

  const [newKeyword, setNewKeyword] = useState('');
  const [formChanged, setFormChanged] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    dispatch(fetchPreferences());
    dispatch(fetchCategories());
    dispatch(fetchNewsCountries());
  }, [dispatch]);

  useEffect(() => {
    if (preferences) {
      setForm({
        categories: preferences.categories || [],
        keywords: preferences.keywords || [],
        countries: preferences.countries || [],
        email_daily_digest: preferences.email_daily_digest || false,
        email_frequency: preferences.email_frequency || 'daily',
        notification_enabled: preferences.notification_enabled !== false,
      });
    }
  }, [preferences]);

  const toggleCountry = (code) => {
    const next = form.countries.includes(code)
      ? form.countries.filter((c) => c !== code)
      : [...form.countries, code];
    setForm({ ...form, countries: next });
    setFormChanged(true);
    setSaveSuccess(false);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({ ...form, [name]: type === 'checkbox' ? checked : value });
    setFormChanged(true);
    setSaveSuccess(false);
  };

  const toggleCategory = (category) => {
    const newCategories = form.categories.includes(category)
      ? form.categories.filter(c => c !== category)
      : [...form.categories, category];
    setForm({ ...form, categories: newCategories });
    setFormChanged(true);
    setSaveSuccess(false);
  };

  const addKeyword = () => {
    if (!newKeyword.trim() || form.keywords.includes(newKeyword.trim())) return;
    setForm({ ...form, keywords: [...form.keywords, newKeyword.trim()] });
    setNewKeyword('');
    setFormChanged(true);
    setSaveSuccess(false);
  };

  const handleKeywordKeyPress = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); addKeyword(); }
  };

  const removeKeyword = (keyword) => {
    setForm({ ...form, keywords: form.keywords.filter(k => k !== keyword) });
    setFormChanged(true);
    setSaveSuccess(false);
  };

  const savePreferences = () => {
    dispatch(updatePreferences(form))
      .then(() => { setSaveSuccess(true); setFormChanged(false); })
      .catch(() => setSaveSuccess(false));
  };

  if (loading.preferences && !preferences) return <PreferencesScreenSkeleton />;

  if (errors.preferences && !preferences) {
    return (
      <Box sx={{ px: { xs: 2, sm: 1 }, py: 2.2 }}>
        <Alert severity="error" sx={{ borderRadius: '10px' }}>{errors.preferences}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ pb: { xs: 2, sm: 3 }, px: { xs: 2, sm: 1 }, py: 2.2, width: '100%', maxWidth: '100vw', overflowX: 'hidden' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: isMobile() ? 2 : 3 }}>
        <BackButton navigate_to="/legal-news" />
        <Typography
          variant="h5"
          component="h1"
          sx={{
            fontWeight: 700,
            fontFamily: theme.typography.h1?.fontFamily,
            color: theme.palette.text.primary,
            ml: isMobile() ? 0 : 2,
          }}
        >
          {t('news_hub.preferences.title')}
        </Typography>
      </Box>

      <Box
        sx={{
          backgroundColor: theme.palette.background.paper,
          borderRadius: '12px',
          border: `1px solid ${theme.palette.divider}`,
          overflow: 'hidden',
        }}
      >
        <Box sx={{ p: { xs: 2.5, sm: 3 } }}>

          {/* Categories */}
          <Box data-tour="news-pref-categories" sx={{ mb: 4 }}>
            <SectionLabel>{t('news_hub.preferences.categories')}</SectionLabel>
            {loading.categories ? (
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 1.5 }}>
                {[...Array(9)].map((_, i) => <CategorySkeletonItem key={i} />)}
              </Box>
            ) : (
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 1.5 }}>
                {categories.map(category => {
                  const isSelected = form.categories.includes(category);
                  return (
                    <Box
                      key={category}
                      onClick={() => toggleCategory(category)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        px: 1.5,
                        py: 1.25,
                        borderRadius: '8px',
                        cursor: 'pointer',
                        border: `1px solid ${isSelected ? alpha(theme.palette.primary.main, 0.4) : theme.palette.divider}`,
                        backgroundColor: isSelected ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                        color: isSelected ? theme.palette.primary.main : theme.palette.text.primary,
                        transition: 'all 0.15s ease',
                        '&:hover': {
                          borderColor: alpha(theme.palette.primary.main, 0.3),
                          backgroundColor: alpha(theme.palette.primary.main, 0.05),
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: 18,
                          height: 18,
                          borderRadius: '50%',
                          flexShrink: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: isSelected ? theme.palette.primary.main : 'transparent',
                          border: `1.5px solid ${isSelected ? theme.palette.primary.main : theme.palette.text.disabled}`,
                          transition: 'all 0.15s ease',
                        }}
                      >
                        {isSelected && <CheckIcon sx={{ fontSize: 12, color: theme.palette.primary.contrastText }} />}
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: isSelected ? 500 : 400 }}>
                        {t(`news_category.${category}`)}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>

          {/* Countries */}
          <Box data-tour="news-pref-countries" sx={{ mb: 4 }}>
            <SectionLabel>{t('news_hub.preferences.countries', 'Countries')}</SectionLabel>
            <Typography variant="caption" sx={{ display: 'block', mb: 1.5, color: theme.palette.text.secondary }}>
              {t('news_hub.preferences.countries_description', 'Select countries to follow legal news from. Leave empty to follow all countries.')}
            </Typography>
            {loading.countries && (!countries || countries.length === 0) ? (
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 1.5 }}>
                {[...Array(6)].map((_, i) => <CategorySkeletonItem key={i} />)}
              </Box>
            ) : (
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 1.5 }}>
                {(countries || []).map((code) => {
                  const isSelected = form.countries.includes(code);
                  return (
                    <Box
                      key={code}
                      onClick={() => toggleCountry(code)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        px: 1.5,
                        py: 1.25,
                        borderRadius: '8px',
                        cursor: 'pointer',
                        border: `1px solid ${isSelected ? alpha(theme.palette.primary.main, 0.4) : theme.palette.divider}`,
                        backgroundColor: isSelected ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                        color: isSelected ? theme.palette.primary.main : theme.palette.text.primary,
                        transition: 'all 0.15s ease',
                        '&:hover': {
                          borderColor: alpha(theme.palette.primary.main, 0.3),
                          backgroundColor: alpha(theme.palette.primary.main, 0.05),
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: 18,
                          height: 18,
                          borderRadius: '50%',
                          flexShrink: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: isSelected ? theme.palette.primary.main : 'transparent',
                          border: `1.5px solid ${isSelected ? theme.palette.primary.main : theme.palette.text.disabled}`,
                          transition: 'all 0.15s ease',
                        }}
                      >
                        {isSelected && <CheckIcon sx={{ fontSize: 12, color: theme.palette.primary.contrastText }} />}
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: isSelected ? 500 : 400, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <span style={{ fontSize: '1.1em' }}>{countryFlag(code)}</span>
                        {countryLabel(code)}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>

          {/* Keywords */}
          <Box data-tour="news-pref-keywords" sx={{ mb: 4 }}>
            <SectionLabel>{t('news_hub.preferences.keywords')}</SectionLabel>

            {form.keywords.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                {form.keywords.map(keyword => (
                  <Chip
                    key={keyword}
                    label={keyword}
                    onDelete={() => removeKeyword(keyword)}
                    size="small"
                    sx={{
                      backgroundColor: alpha(theme.palette.primary.main, 0.1),
                      color: theme.palette.primary.main,
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
                      borderRadius: '6px',
                      fontWeight: 500,
                      '& .MuiChip-deleteIcon': {
                        color: alpha(theme.palette.primary.main, 0.6),
                        '&:hover': { color: theme.palette.primary.main },
                      },
                    }}
                  />
                ))}
              </Box>
            )}

            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <TextField
                value={newKeyword}
                onChange={(e) => setNewKeyword(e.target.value)}
                onKeyPress={handleKeywordKeyPress}
                placeholder={t('news_hub.preferences.add_keyword')}
                size="small"
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    fontSize: '0.875rem',
                  },
                }}
              />
              <Button
                variant="contained"
                onClick={addKeyword}
                startIcon={<AddIcon />}
                sx={{
                  textTransform: 'none',
                  borderRadius: '8px',
                  fontWeight: 500,
                  flexShrink: 0,
                  whiteSpace: 'nowrap',
                }}
              >
                {isMobile() ? t('add_comment') : t('news_hub.preferences.add_keyword')}
              </Button>
            </Box>
          </Box>

          {/* Email digest */}
          <Box data-tour="news-pref-digest" sx={{ mb: 4 }}>
            <SectionLabel>{t('news_hub.preferences.email_digest')}</SectionLabel>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {/* Email digest */}
              <Box
                sx={{
                  p: 2,
                  borderRadius: '10px',
                  border: `1px solid ${theme.palette.divider}`,
                  backgroundColor: theme.palette.action.hover,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Switch
                    checked={form.email_daily_digest}
                    onChange={(e) => {
                      setForm({ ...form, email_daily_digest: e.target.checked });
                      setFormChanged(true);
                      setSaveSuccess(false);
                    }}
                    size="small"
                    color="primary"
                  />
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.25 }}>
                      <EmailOutlinedIcon sx={{ fontSize: 16, color: theme.palette.text.secondary }} />
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {t('news_hub.preferences.email_digest')}
                      </Typography>
                    </Box>
                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                      {t('news_hub.preferences.email_digest_description')}
                    </Typography>
                  </Box>
                </Box>

                {form.email_daily_digest && (
                  <Box sx={{ mt: 2, ml: 6 }}>
                    <Typography variant="caption" sx={{ display: 'block', mb: 0.75, color: theme.palette.text.secondary, fontWeight: 500 }}>
                      {t('news_hub.preferences.email_frequency')}
                    </Typography>
                    <Select
                      name="email_frequency"
                      value={form.email_frequency}
                      onChange={handleChange}
                      size="small"
                      sx={{
                        minWidth: 160,
                        borderRadius: '8px',
                        fontSize: '0.875rem',
                      }}
                    >
                      <MenuItem value="daily">{t('news_hub.preferences.daily')}</MenuItem>
                      <MenuItem value="weekly">{t('news_hub.preferences.weekly')}</MenuItem>
                      <MenuItem value="monthly">{t('news_hub.preferences.monthly')}</MenuItem>
                    </Select>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>

          {/* Success message */}
          {saveSuccess && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                p: 1.5,
                mb: 2,
                borderRadius: '8px',
                backgroundColor: alpha(theme.palette.success.main, 0.1),
                color: theme.palette.success.main,
                border: `1px solid ${alpha(theme.palette.success.main, 0.25)}`,
              }}
            >
              <CheckCircleOutlineIcon sx={{ fontSize: 18 }} />
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {t('news_hub.preferences.saved')}
              </Typography>
            </Box>
          )}

          {/* Action buttons */}
          <Box
            data-tour="news-pref-save"
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 1.5,
              pt: 2.5,
              borderTop: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Button
              variant="outlined"
              onClick={() => navigate('/legal-news')}
              sx={{ textTransform: 'none', borderRadius: '8px', fontWeight: 500 }}
            >
              {t('cancel')}
            </Button>
            <Button
              variant="contained"
              onClick={savePreferences}
              disabled={!formChanged || loading.updatePreferences}
              startIcon={loading.updatePreferences ? <CircularProgress size={16} /> : null}
              sx={{ textTransform: 'none', borderRadius: '8px', fontWeight: 500 }}
            >
              {loading.updatePreferences
                ? t('news_hub.preferences.saving')
                : isMobile() ? t('save') : t('news_hub.preferences.save')}
            </Button>
          </Box>

        </Box>
      </Box>
    </Box>
  );
};

export default NewsPreferencesScreen;
